import { ModalGeneric } from "@/components/utils/ModalGeneric";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import notify from "@/services/notify.tsx";
import Select from "react-select";
import debounce from "lodash.debounce";
import ConfigDesks from "@/services/configDesks";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface ModalSubtarefasProps {
  setSubTarefas: (value: any) => void;
  setIsEdit: (value: boolean) => void;
  idTarefa: any;
  isEdit: boolean;
  subTarefas: any;
}

export default function ModalSubtarefas({
  setSubTarefas,
  setIsEdit,
  isEdit,
  idTarefa,
  subTarefas,
}: ModalSubtarefasProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (subTarefas && !!isEdit) {
      setValue("id_responsavel", {
        value: subTarefas?.id_responsavel,
        label: subTarefas?.responsavel,
      });
      setValue("id_tarefa", subTarefas?.id_tarefa);
      setValue("id", subTarefas?.id);

      setValue("title", subTarefas?.title);
      setValue("description", subTarefas?.description);
      setValue("status", subTarefas?.status);
    }
  }, [subTarefas, isEdit]);

  const loadUsers = useCallback(
    debounce(async (inputValue) => {
      if (inputValue) {
        setLoadingUsers(true);
        const res = await ConfigDesks.getContatosBusca(1, 10, inputValue);
        if (res.status === 200) {
          setUsers(res.data.data);
        }
        setLoadingUsers(false);
      } else {
        setUsers([]);
      }
    }, 300),
    [],
  );

  const handleInputChangeUsers = (inputValue: any) => {
    loadUsers(inputValue);
    return inputValue;
  };

  function formatUserData(users: any) {
    return users.map((user: any) => ({
      label: `${user.name.trim()} ${user.last_name.trim()}`,
      value: user.id,
      image: user.filePath,
    }));
  }

  const onSubmit = async (payload: any) => {
    const toastId = notify.loading("Salvando subtarefa...");
    try {
      setIsSubmiting(true);

      let res;
      payload.id_tarefa = idTarefa;
      payload.id_responsavel = payload.id_responsavel.value;

      if (isEdit) {
        res = await ServiceResumoDesk.subTaskedit(payload, payload?.id);
      } else {
        payload.status = 1;
        res = await ServiceResumoDesk.subTaskadd(payload);
      }
      if (res.status === 200) {
        notify.update(
          toastId,
          "success",
          isEdit ? "Subtarefa atualizada!" : "Subtarefa criada!",
        );
        reset();
        setSubTarefas(null);
        queryClient.invalidateQueries({ queryKey: ["subTarefas"] });
      }
    } catch (error: any) {
      notify.update(
        toastId,
        "error",
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao salvar subtarefa",
      );
    } finally {
      setIsSubmiting(false);
    }
  };

  return (
    <ModalGeneric
      onClose={() => {
        setSubTarefas(null);
        setIsEdit(false);
        reset();
      }}
      size="max-w-[70%] w-full px-4 py-4"
    >
      <form className="space-y-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="text-base font-medium">
          {t("tasks.subtasks.modal.title")}
        </h2>
        <input
          type="text"
          placeholder={t("tasks.subtasks.fields.title.placeholder")}
          required
          className="bg-gray-200 px-2 py-1 w-full rounded-lg"
          {...register("title")}
        />
        <input
          type="text"
          placeholder={t("tasks.subtasks.fields.description.placeholder")}
          required
          className="bg-gray-200 px-2 py-1 w-full rounded-lg"
          {...register("description")}
        />
        <Select
          placeholder={t("tasks.subtasks.fields.responsible.placeholder")}
          required
          {...register("id_responsavel")}
          onChange={(value: any) => setValue("id_responsavel", value)}
          onInputChange={handleInputChangeUsers}
          isLoading={loadingUsers}
          value={watch("id_responsavel")}
          options={formatUserData(users)}
          noOptionsMessage={() => t("common:type_to_search")}
          formatOptionLabel={(user: any) => (
            <div className="flex gap-2 items-start">
              <img
                className="w-5 h-5 rounded-full"
                src={user.image}
                alt={user.label}
              />
              <span>{user.label}</span>
            </div>
          )}
        />
        <div className="flex gap-2">
          <button
            type="button"
            className="bg-red-500 w-full py-1 rounded-lg text-white"
            onClick={() => {
              setSubTarefas(null);
              setIsEdit(false);
              reset();
            }}
          >
            {t("common:cancel")}
          </button>
          <button
            disabled={isSubmiting}
            className="bg-azul-400 w-full py-1 rounded-lg text-white disabled:bg-azul-300"
          >
            {isEdit ? t("common:edit") : t("common:add")}
          </button>
        </div>
      </form>
    </ModalGeneric>
  );
}
